/*
 * Enquiry screening for the contact form.
 *
 * Targets low-effort submissions from real people (job applications, one-word
 * "thanks", empty enquiries) rather than bots — the honeypot already covers
 * scripted submissions. Everything here is client-side because Shopify's
 * `{% form 'contact' %}` posts straight to Shopify; a theme has no server-side
 * hook to validate on. That is an acceptable trade-off for this job: the goal
 * is to make a low-effort submission slightly more effortful, not to stop a
 * determined attacker. Nothing sensitive is gated here.
 *
 * Design rule: never hard-block something that might be a real enquiry.
 * Checks either ask for more detail (the visitor can comply) or offer an
 * override, and anything that stays suspicious is passed through with a
 * `contact[screening]` flag so it can be filtered in the inbox instead.
 */
(() => {
  "use strict";

  const STORAGE_KEY = "dropsafe:form-quality:recent";
  const MAX_HISTORY = 20;

  const FIELDS = {
    body: 'textarea[name="contact[body]"]',
    email: 'input[name="contact[email]"]',
    subject: '[name="contact[inquiry]"]',
    company: '[name="contact[company_name]"]',
    name: 'input[name="contact[name]"]',
    phone: 'input[name="contact[phone]"]',
  };

  // The visitor's own details, which is what autofill puts in a field.
  const AUTOFILL_SOURCES = ["company", "name", "email", "phone"];

  // `:autofill` is the standard name; Safari and older Chromium only know the
  // prefixed one, and an unknown selector throws.
  function isAutofilled(input) {
    return [":autofill", ":-webkit-autofill"].some((selector) => {
      try {
        return input.matches(selector);
      } catch (e) {
        return false;
      }
    });
  }

  function parseJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  // Locale strings arrive HTML-escaped (Shopify escapes `| t` output) but we
  // render them with textContent, so turn entities like &#39; back into text.
  function decodeStrings(source) {
    const decoder = document.createElement("textarea");
    const result = {};
    Object.keys(source || {}).forEach((key) => {
      const value = String(source[key] == null ? "" : source[key]);
      if (value.indexOf("&") === -1) {
        result[key] = value;
        return;
      }
      decoder.innerHTML = value;
      result[key] = decoder.value;
    });
    return result;
  }

  // Joiners, bidi controls and the Arabic tatweel. Deleted outright rather than
  // turned into spaces: they occur *inside* words, so replacing them with a
  // space would split a word in half and stop it matching. Tatweel is the
  // decorative stretch in "وظيــفة", which is the same word as "وظيفة".
  const INVISIBLE = /[\u0640\u061C\u200B-\u200F\u2060\uFEFF]/g;

  // Lower-cases, folds accents, strips punctuation and collapses whitespace so
  // both the visitor's text and the configured terms are compared on the same
  // footing. Accent folding matters for Portuguese: it lets a single term list
  // cover "curriculo" and "currículo", "preco" and "preço". It does the same
  // job in Arabic, where stripping combining marks removes tashkeel and folds
  // hamza forms, so "أبحث" and "ابحث" compare equal. Marks are removed before
  // punctuation, otherwise a decomposed accent would become a space mid-word.
  function normalise(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .replace(/[‘’‛]/g, "'")
      .replace(INVISIBLE, "")
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function wordCount(normalised) {
    return normalised ? normalised.split(" ").length : 0;
  }

  function toList(value) {
    if (Array.isArray(value)) return value;
    return String(value == null ? "" : value).split(",");
  }

  // Matches whole words / whole phrases only, so "intern" never fires on
  // "internal" and "cv" never fires on "cvs-something".
  function buildTermMatcher(terms) {
    const cleaned = toList(terms)
      .map(normalise)
      .filter(Boolean)
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

    if (!cleaned.length) return null;

    return new RegExp("(?:^|\\s)(?:" + cleaned.join("|") + ")(?=\\s|$)", "u");
  }

  // Capture phase on the document, so this runs before anything bound to the
  // form itself and before any document-level listener bubbling afterwards.
  // Apps and analytics tags commonly bind to submit; being first is what makes
  // a blocked submission stoppable.
  function bindSubmit(form, handler) {
    document.addEventListener(
      "submit",
      (event) => {
        if (event.target === form) handler(event);
      },
      true
    );
  }

  // preventDefault alone only cancels the browser's own POST. A script that
  // posts the form with fetch, or a tag that counts a submission, would still
  // run — so a blocked submission stops the event outright.
  function stopSubmission(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function hash(value) {
    let result = 5381;
    for (let i = 0; i < value.length; i += 1) {
      result = ((result << 5) + result + value.charCodeAt(i)) | 0;
    }
    return String(result);
  }

  function readHistory() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeHistory(entries) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_HISTORY)));
    } catch (e) {
      /* private mode / storage full — duplicate detection just goes quiet */
    }
  }

  function setup(configNode) {
    const config = parseJSON(configNode);
    if (!config || !config.formId) return;

    const form = document.getElementById(config.formId);
    if (!form || form.dataset.formQualityBound === "true") return;
    form.dataset.formQualityBound = "true";

    const field = (key) => form.querySelector(FIELDS[key]);
    const honeypot = config.honeypotId ? document.getElementById(config.honeypotId) : null;

    // Browser autofill and password managers ignore autocomplete="off" and can
    // fill the trap with the visitor's own details: Chrome read the old
    // 'Business website' label as a company field and a genuine buyer's
    // enquiry arrived as spam. Only a real browser runs this script, so an
    // autofilled trap is emptied here and the enquiry goes through as normal,
    // carrying a screening flag instead of the alarm. Two signals: the
    // browser's own autofill marker, and a value repeating one of the
    // visitor's other details, which also covers extensions that fill without
    // setting the marker. A scripted POST never gets here and keeps the alarm.
    // Remembered for the page load, because the field is empty by the time a
    // blocked submission is sent again.
    let honeypotAutofilled = false;

    function repeatsVisitorDetail(value) {
      const target = normalise(value);
      if (!target) return false;
      return AUTOFILL_SOURCES.some((key) => {
        const input = field(key);
        return input && normalise(input.value) === target;
      });
    }

    function clearAutofilledHoneypot() {
      if (!honeypot || honeypot.value === "") return;
      if (isAutofilled(honeypot) || repeatsVisitorDetail(honeypot.value)) {
        honeypot.value = "";
        honeypotAutofilled = true;
      }
    }

    // Honeypot cleanup runs whether or not screening is on: a genuine
    // submission must never carry an empty
    // `contact[spam_trap_triggered_do_not_reply]` into the notification email,
    // and a filled one must always survive so the spam stays visible.
    // Symmetric rather than one-way, so a second submit in the same page load
    // re-reads the field instead of inheriting the first one's decision.
    function dropEmptyHoneypot() {
      if (honeypot) {
        honeypot.disabled = honeypot.value === "";
      }
    }

    if (config.enabled === false) {
      bindSubmit(form, () => {
        clearAutofilledHoneypot();
        dropEmptyHoneypot();
      });
      return;
    }

    const strings = decodeStrings(config.strings);
    const screeningField = form.querySelector("[data-form-quality-screening]");
    const renderedAt = Date.now();

    const lowEffortPhrases = new Set(toList(config.lowEffortPhrases).map(normalise).filter(Boolean));
    const jobMatcher = buildTermMatcher(config.jobKeywords);
    const freeEmailDomains = new Set(
      toList(config.freeEmailDomains)
        .map((domain) => String(domain).trim().toLowerCase())
        .filter(Boolean)
    );

    const minChars = Number(config.minChars) || 0;
    const minWords = Number(config.minWords) || 0;
    const minSeconds = Number(config.minSeconds) || 0;
    const duplicateWindowMs = (Number(config.duplicateWindowMinutes) || 0) * 60 * 1000;

    // Both of these are enforced once per page load: after the visitor reads
    // the message and submits again we let it through, so nobody is ever
    // trapped in a loop by a check that guessed wrong.
    let paceWarningShown = false;
    let duplicateWarningShown = false;
    let jobNoticeNode = null;

    function clearErrors() {
      form.querySelectorAll("[data-form-quality-error]").forEach((node) => node.remove());
    }

    function showError(target, message) {
      if (!message) return;
      const anchor = target || form.querySelector(".submit") || form;
      const error = document.createElement("p");
      error.className = "form-quality__error";
      error.setAttribute("data-form-quality-error", "");
      error.setAttribute("role", "alert");
      error.textContent = message;
      anchor.insertAdjacentElement("afterend", error);

      if (target && typeof target.focus === "function") {
        target.focus({ preventScroll: true });
      }
      error.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // The careers deflection is a signpost, not a wall: it links to the right
    // place and keeps a checkbox for anyone whose enquiry only looked like a
    // job application.
    function showJobNotice() {
      if (jobNoticeNode && form.contains(jobNoticeNode)) {
        jobNoticeNode.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      const notice = document.createElement("div");
      notice.className = "form-quality__notice";
      notice.setAttribute("role", "alert");
      notice.setAttribute("data-form-quality-notice", "");

      const title = document.createElement("p");
      title.className = "form-quality__notice-title";
      title.textContent = strings.jobTitle || "";
      notice.appendChild(title);

      const body = document.createElement("p");
      body.textContent = strings.jobBody || "";
      notice.appendChild(body);

      if (config.careersUrl) {
        const link = document.createElement("a");
        link.className = "form-quality__notice-link";
        link.href = config.careersUrl;
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = strings.jobLink || config.careersUrl;
        notice.appendChild(link);
      }

      const label = document.createElement("label");
      label.className = "form-quality__notice-override";

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      // Deliberately unnamed so it is never posted to Shopify.
      checkbox.setAttribute("data-form-quality-override", "");
      label.appendChild(checkbox);

      const labelText = document.createElement("span");
      labelText.textContent = strings.jobOverride || "";
      label.appendChild(labelText);
      notice.appendChild(label);

      const submitRow = form.querySelector(".submit");
      if (submitRow) {
        submitRow.insertAdjacentElement("beforebegin", notice);
      } else {
        form.appendChild(notice);
      }

      jobNoticeNode = notice;
      notice.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    function overrideChecked() {
      const checkbox = form.querySelector("[data-form-quality-override]");
      return Boolean(checkbox && checkbox.checked);
    }

    function emailDomain(value) {
      const parts = String(value || "").toLowerCase().trim().split("@");
      return parts.length === 2 ? parts[1] : "";
    }

    function rememberSubmission(signature) {
      if (!duplicateWindowMs || !signature) return;
      const history = readHistory().filter((entry) => Date.now() - entry.at < duplicateWindowMs);
      history.push({ id: signature, at: Date.now() });
      writeHistory(history);
    }

    function isDuplicate(signature) {
      if (!duplicateWindowMs || !signature) return false;
      return readHistory().some(
        (entry) => entry.id === signature && Date.now() - entry.at < duplicateWindowMs
      );
    }

    const bodyField = field("body");
    // The message block is optional in the theme editor, but an enquiry with no
    // message is exactly what we are trying to stop — let the browser handle
    // "empty" natively and keep this script for "too thin".
    if (bodyField && (minChars > 0 || minWords > 0)) {
      bodyField.required = true;
    }

    bindSubmit(form, (event) => {
      clearErrors();

      const emailField = field("email");
      const subjectField = field("subject");
      const companyField = field("company");

      const bodyValue = bodyField ? bodyField.value : "";
      const normalisedBody = normalise(bodyValue);
      const emailValue = emailField ? emailField.value : "";
      const flags = [];

      clearAutofilledHoneypot();
      if (honeypotAutofilled) {
        flags.push("honeypot-autofilled");
      }

      // 1. A scripted submission. Let it through so the honeypot value lands in
      // the inbox, where its field name spells out what happened. That part is
      // pure markup and needs nothing from this script — the flag below is the
      // belt-and-braces copy for the bots that do run JS, so every machine
      // signal stays in one field regardless of which check caught it.
      if (honeypot && honeypot.value !== "") {
        if (screeningField) {
          screeningField.value = "spam-trap";
          screeningField.disabled = false;
        }
        return;
      }

      // 2. Job applications go to the careers page, not to sales.
      if (jobMatcher && !overrideChecked()) {
        const haystack = normalise(
          [bodyValue, subjectField ? subjectField.value : "", companyField ? companyField.value : ""].join(" ")
        );
        if (jobMatcher.test(haystack)) {
          stopSubmission(event);
          showJobNotice();
          return;
        }
      }
      if (jobMatcher && overrideChecked()) {
        flags.push("job-keyword-override");
      }

      // 3. Enough substance to route to a specialist.
      if (bodyField) {
        const hasText = bodyValue.trim() !== "";
        // "..." and friends survive the browser's required check but carry
        // nothing once punctuation is stripped.
        if (lowEffortPhrases.has(normalisedBody) || (hasText && !normalisedBody)) {
          stopSubmission(event);
          showError(bodyField, strings.lowEffort);
          return;
        }
        const shortText = normalisedBody.replace(/\s/g, "").length < minChars;
        const fewWords = wordCount(normalisedBody) < minWords;
        if (normalisedBody && (shortText || fewWords)) {
          stopSubmission(event);
          showError(bodyField, strings.tooShort);
          return;
        }
      }

      // 4. Submitted faster than anyone could plausibly have read the form.
      if (minSeconds > 0 && !paceWarningShown && Date.now() - renderedAt < minSeconds * 1000) {
        paceWarningShown = true;
        stopSubmission(event);
        showError(form.querySelector(".submit"), strings.tooFast);
        return;
      }

      // 5. The same enquiry sent twice in a row. The signature carries the
      // routing context too — the contact page splits enquiries by product
      // subject, so the same wording sent for Nets and then for Matting is two
      // separate enquiries, not a repeat.
      const signature = hash(
        [
          config.formId,
          window.location.pathname,
          normalise(subjectField ? subjectField.value : ""),
          normalise(emailValue),
          normalisedBody,
        ].join("|")
      );
      // Warned once, then allowed: the history is written when we hand off to
      // Shopify, not when Shopify confirms receipt, so a submission lost to a
      // network error would otherwise be unrepeatable for the whole window.
      if (!duplicateWarningShown && isDuplicate(signature)) {
        duplicateWarningShown = true;
        stopSubmission(event);
        showError(form.querySelector(".submit"), strings.duplicate);
        return;
      }

      // 6. Consumer mailbox. Blocking is opt-in — by default we only flag it,
      // because plenty of genuine buyers in our markets use one.
      const domain = emailDomain(emailValue);
      if (domain && freeEmailDomains.has(domain)) {
        if (config.requireBusinessEmail) {
          stopSubmission(event);
          showError(emailField, strings.businessEmail);
          return;
        }
        flags.push("free-email-domain");
      }

      // Passed. Drop the honeypot so it never clutters a clean notification,
      // and attach the screening flags only when there is something to say.
      dropEmptyHoneypot();
      if (screeningField) {
        if (flags.length) {
          screeningField.value = flags.join(", ");
          screeningField.disabled = false;
        } else {
          screeningField.disabled = true;
        }
      }

      rememberSubmission(signature);
    });
  }

  function init() {
    document.querySelectorAll("script[data-form-quality-config]").forEach(setup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
